const camelize = (str) => str.replace(/-([a-z])/g, (c) => c[1].toUpperCase());
const kebabize = (string) => string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

const MAX_SETTINGS_LENGTH = 350;

const MAX_PRODUCTS = 10;

const isValidNumberOfActions = (value) => {
  const parsedValue = parseInt(value, 10);
  return Number.isInteger(parsedValue) && parsedValue >= 0 && parsedValue <= Number.MAX_SAFE_INTEGER;
};

const isValidNumberOfProducts = (value) => {
  const parsedValue = parseInt(value, 10);
  return Number.isInteger(parsedValue) && parsedValue >= 0 && parsedValue <= MAX_PRODUCTS;
};

const VALID_TOGGLE_VALUES = ['on', 'off', 'true', 'false'];
const isValidToggleValue = (value) => VALID_TOGGLE_VALUES.includes(value);

const PLAYER_SETTINGS = {
  products: {
    defaultValue: true,
    isValidValue: (value) => isValidNumberOfProducts(value) || isValidToggleValue(value),
    getValue: (value) => {
      if (value === 'on' || value === 'true') {
        return MAX_PRODUCTS;
      }

      if (value === 'off' || value === 'false') {
        return 0;
      }

      const parsedValue = parseInt(value, 10);

      return Number.isInteger(parsedValue)
        ? Math.min(Math.max(parsedValue, 0), MAX_PRODUCTS)
        : MAX_PRODUCTS;
    },
  },
  title: {
    defaultValue: true,
    isValidValue: (value) => isValidToggleValue(value),
    getValue: (value) => value === 'on' || value === 'true',
  },
  overlayTextWrap: {
    defaultValue: true,
    isValidValue: (value) => isValidToggleValue(value),
    getValue: (value) => value === 'on' || value === 'true',
  },
  productCardMode: {
    defaultValue: 'default',
    isValidValue: (value) => ['default', 'thumbnail', 'full'].includes(value),
    getValue: (value) => value,
  },
  actions: {
    defaultValue: undefined,
    isValidValue: (value) => isValidNumberOfActions(value) || isValidToggleValue(value) || value === 'undefined',
    getValue: (value) =>
      value === 'on' ||
      value === 'true' ||
      (Number.isInteger(parseInt(value, 10)) && parseInt(value, 10) > 0) ||
      (value === 'undefined' ? undefined : false),
  },
  closedCaptions: {
    defaultValue: true,
    isValidValue: (value) => isValidToggleValue(value),
    getValue: (value) => value === 'on' || value === 'true',
  },
  showScrollbars: {
    defaultValue: false,
    isValidValue: (value) => isValidToggleValue(value),
    getValue: (value) => value === 'on' || value === 'true',
  },
  expandButtonVisibility: {
    defaultValue: 'never',
    isValidValue: (value) => ['never', 'always', 'hover'].includes(value),
    getValue: (value) => value,
  },
};

export const parsePlayerSettingValue = (key, value) => {
  const camelizedKey = camelize(key);
  if (!(camelizedKey in PLAYER_SETTINGS)) {
    console.warn(`Invalid setting name: ${key}`);
    return;
  }

  if (!PLAYER_SETTINGS[camelizedKey].isValidValue(value)) {
    console.warn(`Invalid setting ${key} value: ${value}`);
    return;
  }

  return PLAYER_SETTINGS[camelizedKey].getValue(value);
};

export const parsePlayerSettings = (settingsString) => {
  if (MAX_SETTINGS_LENGTH < settingsString.length) {
    console.warn(`Settings string is too long: ${settingsString.length} characters`);
    return {};
  }

  const parsedSettings = {};

  const settingStrings = settingsString.split(';').filter(setting => setting);
  for (const settingString of settingStrings) {
    const settingKVEntry = settingString.split(':').map((s) => s.trim());
    if (settingKVEntry.length !== 2) {
      console.warn(`Invalid settings entry: ${settingKVEntry}`);
      continue;
    }

    const [key, value] = settingKVEntry;

    const parsedValue = parsePlayerSettingValue(key, value);

    if (parsedValue === undefined) {
      // Unknown key or invalid value, ignoring this setting
      continue;
    }

    parsedSettings[camelize(key)] = parsedValue;
  }

  return parsedSettings;
};

export const getSettingsString = (settings) =>
  Object.entries(settings)
    .map(([key, value]) => `${kebabize(key)}:${value}`)
    .join(';');

export const getDefaultPlayerSettings = () =>
  Object.fromEntries(Object.entries(PLAYER_SETTINGS).map(([key, { defaultValue }]) => [key, defaultValue]));
